import React, { useEffect, useState } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Skeleton, Chip,
} from "@mui/material";
import { botsApi } from "../../../api/bots.js";

interface Session {
  session_id:     string;
  created_at:     string;
  last_active_at: string;
  message_count:  number;
  session_type:   string;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

function timeSince(iso: string) {
  const t = new Date(iso).getTime();
  if (!iso || isNaN(t)) return "—";
  const diff = Date.now() - t;
  const mins  = Math.floor(diff / 60_000);
  if (mins < 1)  return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export function ConversationsTab({ botId }: { botId: string }) {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    botsApi.listSessions(botId)
      .then(d => setSessions(d.sessions))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [botId]);

  if (loading) {
    return (
      <Box>
        {[1,2,3,4].map(i => <Skeleton key={i} variant="rounded" height={48} sx={{ mb: 1 }} />)}
      </Box>
    );
  }

  if (sessions.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <Typography color="text.secondary">No conversations yet. Share your bot to get started.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        {sessions.length} conversation{sessions.length !== 1 ? "s" : ""} — most recent first
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Started</TableCell>
              <TableCell>Last Active</TableCell>
              <TableCell align="right">Messages</TableCell>
              <TableCell>Type</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sessions.map(s => (
              <TableRow key={s.session_id} hover>
                <TableCell>
                  <Typography variant="body2">{formatDate(s.created_at)}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{timeSince(s.last_active_at)}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">{s.message_count ?? 0}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={s.session_type ?? "direct"}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: 11, height: 20 }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
